/**
 * custom-collection-view.js
 *
 * Powers the custom-collection-view.liquid section.
 * Fetches data from the proxy, renders theme-defined sections, product grids,
 * and handles "Load more" pagination per section.
 *
 * Works in two modes, decided once at init() from the section's data
 * attributes (data-page-type / data-search-terms, set in Liquid from
 * request.page_type / search.terms):
 *   - 'collection' — original behaviour, fetches /api/collection/:handleId
 *   - 'search'     — fetches /api/search/query with the search term and,
 *                     optionally, a vehicleNodeId when the vehicle filter is
 *                     used on the search results page
 */

(() => {
  'use strict';

  // ═══════════════════════════════════════════════════════════════
  // redirect
  // ═══════════════════════════════════════════════════════════════

  const LEGACY_URL_REDIRECTS = [
    //https://maxtondesign.com/pl/collections/maxton-set
    {
      match: 'maxton-set',
      filtersToAdd: {
        en: '?filter.collection=sets',
        pl: '?filter.collection=sety',
        fr: '?filter.collection=sets',
        de: '?filter.collection=sets-auswahlen',
      },
    },
    //https://maxtondesign.com/pl/collections/splittery-przednie
    {
      match: 'splittery-przednie',
      filtersToAdd: {
        en: '',
        pl: '?filter.collection=splittery-przednie',
        fr: '',
        de: '',
      },
    },
    {
      match: 'front-splitter',
      filtersToAdd: {
        en: '?filter.collection=front-splitters',
        pl: '',
        fr: '',
        de: '',
      },
    },
    {
      match: 'lame-du-pare-chocs',
      filtersToAdd: {
        en: '',
        pl: '',
        fr: '?filter.collection=splitters-avant',
        de: '',
      },
    },
    {
      match: 'front-diffusor',
      filtersToAdd: {
        en: '',
        pl: '',
        fr: '',
        de: '?filter.collection=frontsplitter',
      },
    },
    //https://maxtondesign.com/pl/collections/splittery-tylne
    {
      match: 'splittery-tylne',
      filtersToAdd: {
        en: '',
        pl: '?filter.collection=splittery-tylne',
        fr: '',
        de: '',
      },
    },
    {
      match: 'rear-splitter',
      filtersToAdd: {
        en: '?filter.collection=rear-splitters',
        pl: '',
        fr: '',
        de: '',
      },
    },
    {
      match: 'arriere-splitter',
      filtersToAdd: {
        en: '',
        pl: '',
        fr: '?filter.collection=splitters-arriere',
        de: '',
      },
    },
    {
      match: 'hinten-splitter',
      filtersToAdd: {
        en: '',
        pl: '',
        fr: '',
        de: '?filter.collection=hecksplitter',
      },
    },
    //https://maxtondesign.com/pl/collections/splittery-tylne-boczne
    {
      match: 'splittery-tylne-boczne-1',
      filtersToAdd: {
        en: '',
        pl: '?filter.collection=splittery-tylne-boczne',
        fr: '',
        de: '',
      },
    },
    {
      match: 'lames-de-pare-chocs-arriere-laterales',
      filtersToAdd: {
        en: '',
        pl: '',
        fr: '?filter.collection=splitters-lateraux-arriere',
        de: '',
      },
    },
    {
      match: 'heck-diffusor-seite',
      filtersToAdd: {
        en: '',
        pl: '',
        fr: '',
        de: '?filter.collection=seitliche-splitter-hinten',
      },
    },
    {
      match: 'rear-side-splitters',
      filtersToAdd: {
        en: '?filter.collection=rear-side-splitters',
        pl: '',
        fr: '',
        de: '',
      },
    },
    // https://maxtondesign.com/pl/collections/dokladki-progow-1?filter.collection=dokladki-progow
    {
      match: 'dokladki-progow',
      filtersToAdd: {
        en: '',
        pl: '?filter.collection=dokladki-progow',
        fr: '',
        de: '',
      },
    },
    {
      match: 'side-skirts',
      filtersToAdd: {
        en: '?filter.collection=side-skirt-extensions',
        pl: '',
        fr: '',
        de: '',
      },
    },
    {
      match: 'des-bas-de-caisse',
      filtersToAdd: {
        en: '',
        pl: '',
        fr: '?filter.collection=extensions-de-bas-de-caisse',
        de: '',
      },
    },
    {
      match: 'seitenschweller-diffusor',
      filtersToAdd: {
        en: '',
        pl: '',
        fr: '',
        de: '?filter.collection=schwellerverkleidungen',
      },
    },
    //https://maxtondesign.com/collections/dokladki-tylne
    {
      match: 'dokladki-tylne',
      filtersToAdd: {
        en: '',
        pl: '?filter.collection=dyfuzory-tylne',
        fr: '',
        de: '',
      },
    },
    {
      match: 'rear-valance',
      filtersToAdd: {
        en: '?filter.collection=rear-diffusers',
        pl: '',
        fr: '',
        de: '',
      },
    },
    {
      match: 'diffuseur-arriere',
      filtersToAdd: {
        en: '',
        pl: '',
        fr: '?filter.collection=diffuseurs-arriere',
        de: '',
      },
    },
    {
      match: 'heckansatz',
      filtersToAdd: {
        en: '',
        pl: '',
        fr: '',
        de: '?filter.collection=heckansatz',
      },
    },
    //https://maxtondesign.com/pl/collections/spoilery
    {
      match: 'spoilers',
      filtersToAdd: {
        en: '?filter.collection=spoiler',
        pl: '?filter.collection=spoiler',
        fr: '?filter.collection=spoiler',
        de: '?filter.collection=spoiler',
      },
    },
    //https://maxtondesign.com/pl/collections/spoiler-cap
    {
      match: 'spoiler-cap',
      filtersToAdd: {
        en: '?filter.collection=spoiler-cap',
        pl: '?filter.collection=spoiler-cap',
        fr: '?filter.collection=spoiler-cap',
        de: '?filter.collection=spoiler-cap',
      },
    },
    //https://maxtondesign.com/collections/abs-czarny-polysk-i-mat?filter.material=ABS+Gloss+Black&filter.material=ABS+Matt
    {
      match: 'abs-czarny-polysk-i-mat',
      filtersToAdd: {
        en: '',
        pl: '?filter.material=ABS+Gloss+Black&filter.material=ABS+CNC+Matt',
        fr: '',
        de: '',
      },
    },
    {
      match: 'abs-gloss-and-matt-black',
      filtersToAdd: {
        en: '?filter.material=ABS+Gloss+Black&filter.material=ABS+CNC+Matt',
        pl: '',
        fr: '',
        de: '',
      },
    },
    {
      match: 'abs-materiel-brillant-et-mat-noir',
      filtersToAdd: {
        en: '',
        pl: '',
        fr: '?filter.material=ABS+Gloss+Black&filter.material=ABS+CNC+Matt',
        de: '',
      },
    },
    {
      match: 'abs-material-glanzend-und-matt-schwarz',
      filtersToAdd: {
        en: '',
        pl: '',
        fr: '',
        de: '?filter.material=ABS+Gloss+Black&filter.material=ABS+CNC+Matt',
      },
    },
    //https://maxtondesign.com/collections/carbon?filter.material=Prepreg+Carbon+Fiber
    {
      match: 'carbon',
      filtersToAdd: {
        en: '?filter.material=Prepreg+Carbon+Fiber',
        pl: '?filter.material=Prepreg+Carbon+Fiber',
        fr: '?filter.material=Prepreg+Carbon+Fiber',
        de: '?filter.material=Prepreg+Carbon+Fiber',
      }
    }
  ];

  const getLocaleFromUrl = () => {
    const match = window.location.pathname.match(/^\/(pl|de|fr|en)\//);
    return match ? match[1] : 'pl';
  };


  const applyLegacyUrlRedirect = () => {
    const fullUrl = window.location.href.replace(/\/$/, '');
    const path = window.location.pathname.replace(/\/$/, '');
    if (window.location.search.includes('?')) return false;
    if (window.location.search.includes('filter.')) return false;
    const collectionMatch = path.match(/^\/(?:[a-z]{2}\/)?collections\/([^\/]+)$/);
    if (!collectionMatch) return false;
    const collectionName = collectionMatch[1];
    const redirectEntry = LEGACY_URL_REDIRECTS.find(
      (entry) => entry.match === collectionName
    );
    if (!redirectEntry) return false;
    const locale = getLocaleFromUrl();
    const filterToAdd =
      redirectEntry.filtersToAdd[locale] || redirectEntry.filtersToAdd['en'];

    const finalUrl = fullUrl + filterToAdd;

    window.location.replace(finalUrl);
    return true;
  };

  applyLegacyUrlRedirect();



  // ═══════════════════════════════════════════════════════════════
  // CONFIG
  // ═══════════════════════════════════════════════════════════════

  const API_BASE_URL = 'https://proxy-production-3d1c.up.railway.app';
  const CATEGORY_FILTER_PARAM = 'filter.collection';
  const MATERIAL_FILTER_PARAM = 'filter.material';

  // Translated material names that may appear in URLs -> real material value
  // sent to the API (the URL itself stays unchanged). Add new translations here
  // (matching ignores letter case).
  const MATERIAL_FILTER_ALIASES = {
    'ABS CNC Matt': 'ABS CNC Mat',
  };

  // Set to false before deploying to production — silences all debug logs
  // below without having to manually find/remove console.log calls.
  const DEBUG = false;

  const debugLog = (...args) => {
    if (!DEBUG) return;
    console.log('[custom-collection-view]', ...args);
  };

  const CARET_ICON_SVG = `
    <svg class="icon icon-caret" viewBox="0 0 10 6">
      <path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"></path>
    </svg>
  `.trim();

  const textRoot = document.getElementById('custom-collection-app');
  const {
    loadMoreLabel,
    refineSelectionLabel,
    noMatchMessage,
    resetFiltersDescription,
    resetFiltersLabel,
    categoryPlaceholder,
    materialPlaceholder,
    loadMoreLoading
  } = textRoot.dataset;

  const textLabels = {
    loadMoreLabel,
    refineSelectionLabel,
    resetFiltersDescription,
    noMatchMessage,
    resetFiltersLabel,
    categoryPlaceholder,
    materialPlaceholder,
    loadMoreLoading: loadMoreLoading || "Loading..."
  };
  const {country, language} = textRoot.dataset;

  // ═══════════════════════════════════════════════════════════════
  // RACE-CONDITION GUARD
  //
  // Every load (initial page load OR a vehicle-filter-changed event) gets an
  // incrementing token. Before touching the DOM — and even mid-loop, since
  // renderApp awaits a network call per product card — we check whether a
  // NEWER load has started in the meantime. If so, we abandon the stale one
  // immediately rather than let it finish and overwrite a newer, correct
  // render. This is what stops "rapid filter changes cause several reloads
  // in sequence" — only the LATEST request is ever allowed to reach the DOM.
  //
  // We also abort the in-flight fetch of a superseded request via
  // AbortController, so we're not even wasting the network round-trip.
  // ═══════════════════════════════════════════════════════════════

  const loadGuard = (() => {
    let currentToken = 0;
    let currentAbortController = null;

    const startNewLoad = () => {
      if (currentAbortController) currentAbortController.abort();
      currentToken += 1;
      currentAbortController = new AbortController();
      return { token: currentToken, signal: currentAbortController.signal };
    };

    const isStale = (token) => token !== currentToken;

    return { startNewLoad, isStale };
  })();

  // ═══════════════════════════════════════════════════════════════
  // DOM HELPERS — generic, presentation-only utilities
  // ═══════════════════════════════════════════════════════════════

  const setComponentButtonLabel = (button, label) => {
    const primaryLabel = button.querySelector('.component-button__label');
    const secondaryLabel = button.querySelector('.component-button__label-secondary');

    if (primaryLabel) primaryLabel.textContent = label;
    if (secondaryLabel) secondaryLabel.textContent = label;
  };

  const getComponentButtonLabel = (button) => {
    const primaryLabel = button.querySelector('.component-button__label');
    return primaryLabel ? primaryLabel.textContent.trim() : '';
  };

  const createComponentButton = ({ label = '', style = 'primary', className = '', additionalClasses = '' }) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `component-button ${className} ${additionalClasses}`.trim();

    const content = document.createElement('div');
    content.className = `component-button__content component-button__content--${style} ${additionalClasses}`.trim();
    content.setAttribute('tabindex', '-1');

    const inner = document.createElement('div');
    inner.className = 'component-button__inner';
    inner.setAttribute('tabindex', '-1');

    const labelWrapper = document.createElement('div');
    labelWrapper.className = 'component-button__label-wrapper';

    const primaryLabel = document.createElement('span');
    primaryLabel.className = 'component-button__label';
    primaryLabel.textContent = label;

    const secondaryLabel = document.createElement('span');
    secondaryLabel.className = 'component-button__label-secondary';
    secondaryLabel.setAttribute('aria-hidden', 'true');
    secondaryLabel.textContent = label;

    labelWrapper.append(primaryLabel, secondaryLabel);

    const icon = document.createElement('span');
    icon.className = 'component-button__icon component-button__icon--right svg-wrapper';
    icon.innerHTML = CARET_ICON_SVG;

    inner.append(labelWrapper, icon);
    content.appendChild(inner);
    button.appendChild(content);

    return button;
  };

  // Placed next to a "Load more" button once the user has clicked it a few
  // times (from the 4th click onward) and there's STILL more data available —
  // a nudge to narrow the vehicle selection (brand -> model -> ...) instead
  // of loading everything. Idempotent: won't insert a second one if it's
  // already there. Removed together with the Load more button once there's
  // no more data (see the load-more click handler).
  const ensureRefineButton = (loadMoreBtn) => {
    const container = loadMoreBtn.parentElement;
    if (!container || container.querySelector('.refine-selection-btn')) return;

    const refineBtn = createComponentButton({
      label: textLabels.refineSelectionLabel,
      style: 'secondary',
      className: 'refine-selection-btn',
      additionalClasses: 'homepage-hero__form-submit-button',
    });

    refineBtn.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('refine-selection-requested'));
    });

    container.appendChild(refineBtn);
  };

  const showError = (root, message, state = []) => {
    debugLog('state ->', state)
    const skeleton = root.querySelector('[data-role="skeleton"]');
    if (skeleton) skeleton.hidden = true;

    const content = document.getElementById('custom-collection-content');
    if (!content) return;

    content.hidden = false;
    content.innerHTML = `<p class="custom-collection-error">${message}</p>`;
  };

  // ═══════════════════════════════════════════════════════════════
  // NETWORK — all fetch calls in one place
  // ═══════════════════════════════════════════════════════════════

  const fetchJSON = (url, signal) => {
    debugLog('fetchJSON:', url);
    return fetch(url, { headers: { Accept: 'application/json' }, signal }).then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    });
  };

  const getLocalePathPrefix = () => {
    const match = window.location.pathname.match(/^\/(pl|de|fr|en)(?:\/|$)/);
    return match ? `/${match[1]}` : '';
  };

  const buildPrice = (amount, currency) => {
    const value = parseFloat(amount);
  
    switch (currency) {
      case "USD":
        return `$${value.toFixed(2)}`;     
  
      case "EUR":
        return `€${value.toFixed(2)}`;    
  
      case "PLN":
        return `${value.toFixed(2)} zł`;
  
      default:
        return `${value.toFixed(2)} ${currency}`;
    }
  }

   // Variant price and compare-at price (falls back to the product price range)
  const getProductPricing = (product) => {
    const variant = product.selectedOrFirstAvailableVariant;
    const priceMoney = variant?.price ?? product.priceRange?.minVariantPrice;
    const price = Number.parseFloat(priceMoney?.amount);
    const compareAtCandidates = [variant?.compareAtPrice, product.compareAtPriceRange?.minVariantPrice]
      .map((money) => Number.parseFloat(money?.amount))
      .filter((amount) => Number.isFinite(amount) && amount > price);
    const compareAtPrice = compareAtCandidates[0] ?? null;
    const isOnSale = compareAtPrice !== null;

    return {
      price,
      compareAtPrice,
      currency: priceMoney?.currencyCode,
      isOnSale,
      discountPercent: isOnSale ? Math.round((1 - price / compareAtPrice) * 100) : 0,
    };
  };
  
    const buildPriceHTML = ({ price, compareAtPrice, currency, isOnSale }) =>
      isOnSale
        ? `<s class="price-item price-item--regular">${buildPrice(compareAtPrice, currency)}</s>
           <span class="price-item price-item--sale">${buildPrice(price, currency)}</span>`
        : buildPrice(price, currency);

const buildFitsTo = (fits) => {
  if (!fits) return '';
  try {
    const trimmed = fits.trim();
    const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('[');
    const fitsList = looksLikeJson ? JSON.parse(fits) : fits ;
    if (!Array.isArray(fitsList) || fitsList.length === 0) {
      return `<ul><li>${fits}</li></ul>`;
    }


    const items = fitsList
      .map(item => `<li>${item}</li>`)
      .join('');

    return `<ul>${items}</ul>`;
  } catch (err) {
    console.error('[FITS PARSE ERROR]', err);
    return '';
  }
};


const buildProductTitle = (title, fits) => {
  if (!title) return '';
  const list = buildFitsTo(fits);

  if (title.includes('passend für ')) {
    return `<strong>${title.split('passend für ')[0]}</strong> passend für: ${list}`;
  }

  if (title.includes('suitable for ')) {
    return `<strong>${title.split('suitable for ')[0]}</strong> suitable for: ${list}`;
  }
  if (title.includes('compatible with ')) {
    return `<strong>${title.split('compatible with')[0]}</strong> compatible with: ${list}`;
  }

  if (title.includes('compatible avec ')) {
    return `<strong>${title.split('compatible avec ')[0]}</strong> compatible avec: ${list}`;
  }

  if (title.includes('pasujący do ')) {
    return `<strong>${title.split('pasujący do ')[0]}</strong> pasujący do: ${list}`;
  }

  if (title.includes('pasujące do ')) {
    return `<strong>${title.split('pasujące do ')[0]}</strong> pasujące do: ${list}`;
  }

  if (title.includes('pasująca do ')) {
    return `<strong>${title.split('pasująca do ')[0]}</strong> pasująca do: ${list}`;
  }

  return `<strong>${title}</strong>`;
};

  const wrapper = document.querySelector('.product-card-container-reader');

  const buildSrcset = (url) => `
  ${url}?width=165 165w,
  ${url}?width=360 360w,
  ${url}?width=533 533w,
  ${url}?width=720 720w,
  ${url}?width=940 940w,
  ${url}?width=1066 1066w,
  ${url} 2000w`;
  const parseFitsList = (fitsValue) => {
    if (!fitsValue) return [];
    const trimmed = fitsValue.trim();
    if (!trimmed.startsWith('[')) return [trimmed];
    try {
      const list = JSON.parse(trimmed);
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  };

  const resolveProductFits = (product, collectionFits) => {
    const productFitsValue = product.fits?.value;
    if (parseFitsList(productFitsValue).length === 1) return productFitsValue;
    return collectionFits?.value ?? productFitsValue;
  };

  const createProductCardHTML = (product, collection,fits) => {
    const clone = wrapper.cloneNode(true);
    clone.classList.remove('hidden')
    if(product.tags.indexOf('SET') < 0) {
      clone.querySelector('.card__badge').classList.add('hidden')
      clone.querySelector('.quick_add.js-item').classList.remove('hidden')
    }
    const pricing = getProductPricing(product);
    const priceElement = clone.querySelector('.price');
    priceElement?.classList.toggle('price--on-sale', pricing.isOnSale);
    priceElement?.classList.remove('price--sold-out');
    clone.querySelector('.card__badge')?.classList.toggle('price-compare', pricing.isOnSale);
    clone.querySelector('.price')?.classList.toggle('price--on-sale', pricing.isOnSale);
    const path = collection ? `/collections/${collection}/products/${product.handle}` : `/products/${product.handle}`;
    const prodFits = resolveProductFits(product, fits);
    const data = {
      url: path,
      handle: product.handle,
      img: product.featuredImage?.url,
      title: product.title,
      pricing,
      id: product.id,
      fits: prodFits
    };
    
    clone.querySelectorAll('.js-item').forEach(el => {
      const field = el.dataset.field;

      switch (field) {
        case 'url':
          el.href = data.url;
          el.setAttribute('aria-describedby', `CardLink-${data.id}`);
          if (el.classList.contains('js-switch')) {
            el.innerHTML = buildProductTitle(data.title,data.fits);
          }
        break;

        case 'img':
          const newUrl = data.img;
          const secondUrl = product.images.nodes.length > 2 ? product.images?.nodes?.at(-2)?.url : product.images?.nodes?.at(-1)?.url;
          const imgUrl = product.hover_img?.reference.image.url ? product.hover_img?.reference.image.url : secondUrl;
          el.src = newUrl;
          el.alt = product.featuredImage?.altText || product.title;
          el.srcset = buildSrcset(newUrl);

          if (imgUrl) {
            const hoverImg = document.createElement('img');
            hoverImg.className = 'img-custom-hover';
            hoverImg.src = imgUrl;
            hoverImg.alt = product.title;
            hoverImg.srcset = buildSrcset(imgUrl);
            el.parentElement.appendChild(hoverImg);
          }
        break;

        case 'compare':
          el.dataset.compareToggle = data.handle;
        break;

        case 'quick_add':
          const gid = product.selectedOrFirstAvailableVariant.id;
          const id = gid.split("/").pop().split("#")[0];
          el.dataset.compareAddToCart = id;
        break;

        case 'price':
          el.textContent = buildPrice(data.pricing.price, data.pricing.currency);
        break;

        case 'compare-price':
          el.textContent = data.pricing.isOnSale ? buildPrice(data.pricing.compareAtPrice, data.pricing.currency) : '';
        break;

        case 'sale-price':
          el.textContent = buildPrice(data.pricing.price, data.pricing.currency);
        break;

        case 'discount':
          el.textContent = data.pricing.isOnSale ? `-${data.pricing.discountPercent}%` : '';
          el.hidden = !data.pricing.isOnSale;
        break;
      }
    });

    const heading = clone.querySelector('.card__heading')
    heading.removeAttribute('data-toggle-checked');

    return clone.outerHTML;
  }

  const fetchProductCardHTML = async (product, collectionHandle, fits, signal) => {
    const html = createProductCardHTML(product, collectionHandle, fits);
    return html;
  };

  const fetchHeroCollection = async (handle, signal) => {

    const res = await fetch(`${getLocalePathPrefix()}/collections/${handle}?section_id=ajax-hero-collection-banner`, { signal });
    return res.text();
  };

  const fetchBreadcrumbs = async (handle, signal) => {
    const res = await fetch(`${getLocalePathPrefix()}/collections/${handle}?section_id=ajax-breadcrumbs`, { signal });
    return res.text();
  };

  // ═══════════════════════════════════════════════════════════════
  // DATA TRANSFORMATION — turning the raw API payload into
  // theme-ready section data
  // ═══════════════════════════════════════════════════════════════

  const flattenProducts = (categories) =>
    categories.flatMap((cat) =>
      cat.products.map((p) => ({ ...p, _handles: cat.handles || [] }))
    );

  // Groups the flattened product list into theme-defined sections, and attaches
  // the correct pagination cursor for each section by matching its handles back
  // to the category the backend returned that data for.
  const mapSections = (sections, categoriesRaw, allProducts, limit) =>
    sections.map((section) => {
      const handles = section.handles.map((h) => h.trim().toUpperCase());

      const products = allProducts.filter((p) =>
        p._handles.some((h) => handles.includes(h.toUpperCase()))
      );

      const sortedHandles = [...handles].sort().join(',');
      const sourceCategory = categoriesRaw.find(
        (cat) => [...cat.handles].sort().join(',').toUpperCase() === sortedHandles
      );

      return {
        title: section.title,
        description: section.description,
        handles,
        products: products.slice(0, limit),
        hasMore: sourceCategory ? sourceCategory.hasNextPage : false,
        endCursor: sourceCategory ? sourceCategory.endCursor : null,
      };
    });

  // ═══════════════════════════════════════════════════════════════
  // CATEGORY FILTER — multi-select checkboxes ("Splittery", "Pozostałe"...),
  // synced to the URL via ?filter.collection=<value> (repeatable param).
  //
  // Design: URL is the source of truth, same pattern as vehicle-filter-changed
  // elsewhere in this app. We use history.replaceState (not pushState) here,
  // since toggling checkboxes repeatedly shouldn't spam browser history the
  // way a real navigation step (choosing a car model) should.
  //
  // Default: nothing in the URL -> every available section is checked/shown.
  // If the URL has one or more filter.collection values -> only sections
  // matching those values are checked/shown (the rest still appear in the
  // dropdown, just unchecked).
  // ═══════════════════════════════════════════════════════════════
  // Polish-aware slug fallback, used when a section has no explicit
  // `filterValue`. Prefer adding an explicit `filter_value` setting per
  // category block in the section schema — it's more stable than deriving
  // from the title (title changes shouldn't silently break shared links).
  const slugify = (str) =>
    (str || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ł/gi, 'l')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const getSectionFilterValue = (section) => section.filterValue || slugify(section.title);

  const getActiveCategoryFiltersFromUrl = (filter) =>
    new URLSearchParams(window.location.search).getAll(filter);

  const resolveMaterialAlias = (material) => {
    const normalizedMaterial = material.trim().toLowerCase();
    const aliasEntry = Object.entries(MATERIAL_FILTER_ALIASES).find(([alias]) => alias.toLowerCase() === normalizedMaterial);
    return aliasEntry ? aliasEntry[1] : material;
  };

  // Materials sent to the API (aliases resolved); the URL keeps the original values
  const getActiveMaterialsFromUrl = () =>
    getActiveCategoryFiltersFromUrl(MATERIAL_FILTER_PARAM).map(resolveMaterialAlias);

  const setActiveCategoryFiltersInUrl = (values) => {
    const params = new URLSearchParams(window.location.search);
    params.delete(CATEGORY_FILTER_PARAM);
    values.forEach((value) => params.append(CATEGORY_FILTER_PARAM, value));

    const query = params.toString();
    const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    history.replaceState(history.state, '', newUrl);
  };

  // Same idea, for the material checkboxes (moved here from the vehicle-select
  // script now that material is checkboxes too, not a <select> — this keeps
  // BOTH filter checkbox groups' URL-sync logic co-located in one file).
  const setActiveMaterialFiltersInUrl = (values) => {
    const params = new URLSearchParams(window.location.search);
    params.delete(MATERIAL_FILTER_PARAM);
    values.forEach((value) => params.append(MATERIAL_FILTER_PARAM, value));

    const query = params.toString();
    const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    history.replaceState(history.state, '', newUrl);
  };

  // Clears only the material param from the URL — used when resetting due to
  // a material that no longer matches anything for the current vehicle.
  const clearMaterialFilterInUrl = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete(MATERIAL_FILTER_PARAM);

    const query = params.toString();
    const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    history.replaceState(history.state, '', newUrl);
  };

  // IMPORTANT: category and material checkboxes now share the exact same
  // class (`multi-select__option`), since material was migrated off a
  // <select> onto the same checkbox markup as category. Every selector that
  // used to just mean "the category checkboxes" now needs to explicitly
  // EXCLUDE `.material`, or it would scoop up material selections too.
  const getCheckedCategoryValues = () =>
    Array.from(document.querySelectorAll('.multi-select__option:checked'))
      .filter((el) => !el.closest('.material'))
      .map((el) => el.value);

  const getCheckedMaterialValues = () =>
    Array.from(document.querySelectorAll('.material .multi-select__option:checked'))
      .map((el) => el.value);

  // Shows/hides already-rendered sections client-side — no re-fetch needed,
  // since every section's products were already loaded in the initial call.
  //
  // NOTE: we set `display` inline in ADDITION to `.hidden`, because the
  // native `[hidden] { display: none }` UA rule has low specificity and gets
  // silently overridden by any theme CSS that sets `display` on
  // `.custom-collection-section` (e.g. `display: flex`/`grid` for layout).
  // Inline style always wins over a class selector, so this is the reliable
  // part; `.hidden` stays too, for correct semantics/accessibility.
  const setSectionHidden = (sectionEl, shouldHide) => {
    sectionEl.hidden = shouldHide;
    sectionEl.style.display = shouldHide ? 'none' : '';
  };

  const applyCategoryFilterVisibility = (checkedValues) => {
    document.querySelectorAll('[data-filter-value]').forEach((sectionEl) => {
      const isAnyFilterActive = checkedValues.length > 0;
      const shouldHide = isAnyFilterActive && !checkedValues.includes(sectionEl.dataset.filterValue);
      setSectionHidden(sectionEl, shouldHide);
    });
  };

  // ── "No matches" empty state (category checkboxes hide everything) ─────
  // Shown when the currently checked category filters hide EVERY
  // rendered section (e.g. filtered to "Dyfuzory" on a model that has no
  // diffuser products). Rebuilt fresh after every DOM swap, since
  // swapContentWithFade wipes #custom-collection-content's innerHTML.
  const NO_MATCH_MESSAGE_ID = 'category-filter-empty-state';

  // Builds the options object loadCollection() expects, based on the
  // current mode (read from root.dataset) plus whatever overrides are
  // passed in (e.g. a newly picked vehicleNodeId). Centralised here so
  // every call site — init, material change, reset — builds this the same
  // way instead of duplicating the branch.
  const buildLoadOptions = (root, overrides = {}) => {
    const { handle = '', handleId, generation, pageType, searchTerms } = root.dataset;
    const limit = generation ? 500 : parseInt(root.dataset.productsPerCategory || '0', 10);

    if (pageType === 'search') {
      return {
        mode: 'search',
        handle: '',
        handleId: null,
        searchTerm: searchTerms,
        vehicleNodeId: window.__collectionState?.vehicleNodeId ?? null,
        limit,
        ...overrides,
      };
    }

    return {
      mode: 'collection',
      handle,
      handleId,
      limit,
      ...overrides,
    };
  };

  const resetCategoryFilters = () => {
    document.querySelectorAll('.multi-select:not(.material) .multi-select__option').forEach((input) => {
      input.checked = true;
    });
    setActiveCategoryFiltersInUrl([]);
    applyCategoryFilterVisibility([]);
    updateMultiSelectToggleLabel('category');
    updateMultiSelectToggleLabel('material');
    updateNoMatchVisibility();
  };

  const ensureNoMatchMessage = () => {
    let el = document.getElementById(NO_MATCH_MESSAGE_ID);
    if (el) return el;

    const content = document.getElementById('custom-collection-content');
    if (!content) return null;

    el = document.createElement('div');
    el.id = NO_MATCH_MESSAGE_ID;
    el.className = 'custom-collection-empty custom-collection-empty--filtered';
    el.hidden = true;

    const text = document.createElement('p');
    text.textContent = textLabels.resetFiltersDescription;

    const resetBtn = createComponentButton({
      label: textLabels.resetFiltersLabel,
      className: 'reset-category-filter-btn',
      additionalClasses: 'homepage-hero__form-submit-button',
    });
    resetBtn.addEventListener('click', resetCategoryFilters);

    el.append(text, resetBtn);
    content.prepend(el);

    return el;
  };

  const updateNoMatchVisibility = () => {
    const sections = Array.from(document.querySelectorAll('[data-filter-value]'));
    const message = ensureNoMatchMessage();
    if (!message) return;

    const anyVisible = sections.some((el) => !el.hidden);
    message.hidden = sections.length === 0 || anyVisible;
  };

  // ── Full reset (category + material) + real refetch ────────────────────
  // Used for the SERVER-SIDE empty state (zero products returned at all,
  // typically because the active material doesn't exist for this vehicle) —
  // unlike resetCategoryFilters above, this can't just show/hide already-
  // fetched sections, because nothing was fetched. It has to clear both
  // filters and hit the proxy again. Also clears any active vehicle filter
  // (search mode), for the same "start from a clean slate" reason.
  const resetAllFiltersAndReload = (root) => {
    document.querySelectorAll('.multi-select:not(.material) .multi-select__option').forEach((input) => {
      input.checked = true;
    });
    setActiveCategoryFiltersInUrl([]);

    clearMaterialFilterInUrl();
    document.querySelectorAll('.material .multi-select__option').forEach((input) => {
      input.checked = false;
    });

    loadCollection(root, buildLoadOptions(root, { vehicleNodeId: null }));
  };

  // ── Generic toggle label updater ──
  const updateMultiSelectToggleLabel = (type) => {
    const toggle = document.querySelector(`.${type} .multi-select__toggle`);
    if (!toggle) return;

    const textEl = toggle.querySelector('p');
    if (!textEl) return;

    let badge = toggle.querySelector('.multi-select__badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'multi-select__badge';
      toggle.appendChild(badge);
    }

    const allOptions = Array.from(document.querySelectorAll(`.${type} .multi-select__option`));
    const checkedOptions = allOptions.filter((el) => el.checked);

    // Placeholder when nothing selected
    if (checkedOptions.length === 0) {
      textEl.textContent = textLabels[`${type}Placeholder`];
      badge.hidden = true;
      badge.textContent = '';
      return;
    }

    // Collect labels
    const labels = checkedOptions.map((input) => {
      const labelText = input.closest('label')?.textContent ?? input.value;
      return labelText.trim();
    });
    textEl.textContent = labels.join(', ');

    // Show badge only when 2+ selected
    if (checkedOptions.length >= 2) {
      badge.hidden = false;
      badge.textContent = String(checkedOptions.length);
    } else {
      badge.hidden = true;
      badge.textContent = '';
    }
  };


  // Rebuilds the checkbox list to match whatever sections actually rendered
  // (with real titles), and checks the ones implied by the URL.
  //
  // Scoped to `.multi-select:not(.material)` — now that material ALSO uses
  // ".multi-select__dropdown .list-unstyled", the old unscoped selector
  // would randomly grab whichever of the two dropdowns happens to come
  // first in the page's DOM order.
  const renderCategoryFilterUI = (sectionsWithProducts) => {
    const dropdown = document.querySelector('.multi-select:not(.material) .multi-select__dropdown .list-unstyled');
    if (!dropdown) return;

    const urlFilters = getActiveCategoryFiltersFromUrl(CATEGORY_FILTER_PARAM);
    const hasUrlFilters = urlFilters.length > 0;

    dropdown.innerHTML = '';

    sectionsWithProducts.forEach((section) => {
      const value = getSectionFilterValue(section);

      const li = document.createElement('li');
      const label = document.createElement('label');
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.value = value;
      input.className = 'multi-select__option';
      input.checked = hasUrlFilters ? urlFilters.includes(value) : true;

      label.append(input, document.createTextNode(` ${section.title}`));
      li.appendChild(label);
      dropdown.appendChild(li);
    });

    updateMultiSelectToggleLabel('category');
    updateMultiSelectToggleLabel('material');
  };

  // ═══════════════════════════════════════════════════════════════
  // MATERIAL FILTER — same checkbox pattern as category now, instead of a
  // native <select multiple>. Options come ENTIRELY from the backend's
  // per-category `filterOptions` list (label/value/count), aggregated
  // across all returned categories.
  //
  // Unlike category, "nothing checked" for material genuinely means "no
  // material filter" — there's no equivalent of category's "default to all
  // checked", since checking every material wouldn't narrow anything down
  // (it's the same as checking none).
  // ═══════════════════════════════════════════════════════════════

  // Merges filterOptions from every category in the response into one list,
  // summing counts for values that appear in more than one category (the
  // material dropdown is a single, page-wide control, not per-category).
  const collectMaterialFilterOptions = (categoriesRaw) => {
    const merged = new Map();

    (categoriesRaw || []).forEach((cat) => {
      (cat.filterOptions || []).forEach((opt) => {
        if (!opt || !opt.value) return;
        const existing = merged.get(opt.value);
        const count = (existing?.count || 0) + (opt.count || 0);
        merged.set(opt.value, { value: opt.value, label: opt.label || opt.value, count });
      });
    });

    return Array.from(merged.values()).sort((a, b) => a.label.localeCompare(b.label, 'pl'));
  };

  // Rebuilds the material checkbox list from the backend's filterOptions,
  // checking whichever values are currently in the URL. Mirrors
  // renderCategoryFilterUI, but scoped to `.material` and defaulting to
  // NONE checked (rather than category's "all checked by default").
  const renderMaterialFilterUI = (categoriesRaw) => {
    const dropdown = document.querySelector('.material .multi-select__dropdown .list-unstyled');
    if (!dropdown) return;

    const options = collectMaterialFilterOptions(categoriesRaw);
    const urlMaterials = getActiveCategoryFiltersFromUrl(MATERIAL_FILTER_PARAM);
    const currentMaterials = [...urlMaterials, ...getActiveMaterialsFromUrl()];

    dropdown.innerHTML = '';

    options.forEach((opt) => {
      const li = document.createElement('li');
      const label = document.createElement('label');
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.value = opt.value;
      input.className = 'multi-select__option';
      input.checked = currentMaterials.includes(opt.value);

      label.append(input, document.createTextNode(` ${opt.label}`));
      li.appendChild(label);
      dropdown.appendChild(li);
    });

    updateMultiSelectToggleLabel('material');
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDERING — building one section's DOM
  // ═══════════════════════════════════════════════════════════════

  // Fetches every product card in a section IN PARALLEL — these requests are
  // fully independent of each other, so there's no reason to wait for card N
  // to finish before starting card N+1. A failed/aborted individual card
  // resolves to null instead of rejecting the whole batch, so one bad card
  // can't take down the rest of the section.
  //
  // collectionHandle (real Shopify collection.handle, NOT the numeric proxy
  // ID) is threaded through so every card request goes through
  // /collections/{handle}/products/{handle}, giving Liquid a populated
  // `collection` object during that section's render. In search mode this
  // is '' (see loadCollection), so cards link to plain /products/{handle}.
  const fetchSectionCardHtmls = (products, collectionHandle,fits, signal) =>
    Promise.all(
      products.map((product) =>
        fetchProductCardHTML(product, collectionHandle, fits, signal).catch((err) => {
          if (err.name !== 'AbortError') debugLog('card fetch failed for', product.handle, err);
          return null;
        })
      )
    );

  const buildProductGrid = (cardHtmls) => {
    const ul = document.createElement('ul');
    ul.className = 'grid product-grid grid--2-col-tablet-down grid--4-col-desktop';

    cardHtmls.forEach((html, i) => {
      if (html == null) return; // skipped due to error/abort

      const li = document.createElement('li');
      li.className = 'grid__item scroll-trigger animate--slide-in';
      li.style = `--animation-order: ${i + 1};`;

      const wrapperEl = document.createElement('div');
      wrapperEl.innerHTML = html;

      const card = wrapperEl.querySelector('.product-card, .card-product, .grid-product');
      li.appendChild(card || wrapperEl.firstElementChild);

      ul.appendChild(li);
    });

    return ul;
  };

  const buildLoadMoreButton = (section, state) => {
    const btn = createComponentButton({
      label: textLabels.loadMoreLabel,
      className: 'load-more-btn',
      additionalClasses: 'homepage-hero__form-submit-button',
    });

    Object.assign(btn.dataset, {
      sectionHandles: section.handles.join(','),
      cursor: section.endCursor,
      limit: state.productsPerCategory,
      collectionHandle: state.handleId,
      // Real Shopify collection handle — separate from collectionHandle
      // above, which is the numeric ID used for proxy API calls. Needed so
      // "Load more" card fetches also render with a populated `collection`.
      shopifyHandle: state.handle,
      // Carried so the load-more click handler (which only has the button,
      // not the full state) knows which endpoint/params to use.
      mode: state.mode,
      searchTerm: state.searchTerm || '',
      vehicleNodeId: state.vehicleNodeId || '',
      ulId: Math.random().toString(36).substring(2),
    });

    debugLog('section.endCursor ->', state.productsPerCategory);

    return btn;
  };

  const buildSectionElement = async (section, state, signal) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'custom-collection-section';

    const filterValue = getSectionFilterValue(section);
    wrapper.dataset.filterValue = filterValue;

    const urlFilters = getActiveCategoryFiltersFromUrl(CATEGORY_FILTER_PARAM);
    if (urlFilters.length > 0) setSectionHidden(wrapper, !urlFilters.includes(filterValue));

    const titleEl = document.createElement('h3');
    titleEl.className = 'custom-collection-section-title';
    titleEl.textContent = section.title;

    const gridContainer = document.createElement('div');
    gridContainer.className = 'product-grid-container page-width';

   
    const cardHtmls = await fetchSectionCardHtmls(section.products, state.handle, state.fits, signal);
    const ul = buildProductGrid(cardHtmls);
    gridContainer.appendChild(ul);

    if (section.hasMore) {
      const btn = buildLoadMoreButton(section, state);
      ul.id = btn.dataset.ulId;
      gridContainer.appendChild(btn);
    }

    wrapper.appendChild(titleEl);

    if (section.description) {
      const descEl = document.createElement('p');
      descEl.className = 'custom-collection-section-description';
      descEl.textContent = section.description;
      wrapper.appendChild(descEl);
    }

    wrapper.appendChild(gridContainer);
    return wrapper;
  };

  // Fire-and-forget: swaps in the hero banner / breadcrumbs sections whenever
  // they resolve, independent of the main product grid render. Only makes
  // sense in collection mode — search results aren't scoped to one real
  // Shopify collection, so there's no hero/breadcrumb section to fetch.
  const refreshAncillarySections = (handle, signal) => {

    debugLog('checkHandle-> ', handle)
    fetchBreadcrumbs(handle, signal).then((html) => {
      const target = document.getElementById('ajax-breadcrumbs');
      if (target) target.outerHTML = html;
    });

    fetchHeroCollection(handle, signal).then((html) => {
      const target = document.getElementById('ajax-hero-collection');
      if (target) target.innerHTML = html;
    });
  };

  const swapContentWithFade = async (content, fragment) => {
    content.classList.add('fade-out');
    await new Promise((resolve) => setTimeout(resolve, 150));

    content.innerHTML = '';
    content.appendChild(fragment);
    content.hidden = false;

    requestAnimationFrame(() => content.classList.remove('fade-out'));
  };

  const renderApp = async (root, state, token, signal) => {
    const skeleton = root.querySelector('[data-role="skeleton"]');
    const content = document.getElementById('custom-collection-content');
    if (!content) return;

    if (skeleton) skeleton.hidden = true;

    // Keep the material checkboxes in sync on EVERY render, including the
    // zero-products case below — that's actually the most common moment a
    // material genuinely doesn't exist for this vehicle.
    renderMaterialFilterUI(state.categoriesRaw);

    // Only keep sections that actually have products — an empty category is
    // skipped entirely (no title, no empty grid), instead of rendering just
    // a heading with nothing underneath it.
    const sectionsWithProducts = state.sections.filter((section) => section.products.length > 0);

    if (!sectionsWithProducts.length) {
      if (loadGuard.isStale(token)) return;

      content.innerHTML = '';

      const emptyEl = document.createElement('div');
      emptyEl.className = 'custom-collection-empty custom-collection-empty--filtered';

      const text = document.createElement('p');
      text.textContent = textLabels.resetFiltersDescription || textLabels.noMatchMessage;

      const resetBtn = createComponentButton({
        label: textLabels.resetFiltersLabel,
        className: 'reset-category-filter-btn',
        additionalClasses: 'homepage-hero__form-submit-button',
      });
      resetBtn.addEventListener('click', () => resetAllFiltersAndReload(root));

      emptyEl.append(text, resetBtn);
      content.appendChild(emptyEl);
      content.hidden = false;
      return;
    }

    const sectionElements = await Promise.all(
      sectionsWithProducts.map((section) => buildSectionElement(section, state, signal))
    );

    if (loadGuard.isStale(token)) {
      debugLog('renderApp aborted after parallel section build (stale token', token, ')');
      return;
    }

    const fragment = document.createDocumentFragment();
    sectionElements.forEach((sectionEl) => fragment.appendChild(sectionEl));

    // Hero banner / breadcrumbs only depend on the collection as a whole,
    // not on any individual section — fetch them once per render, not once
    // per section. Skipped entirely in search mode (see refreshAncillarySections).
    if (state.mode !== 'search') {
      refreshAncillarySections(state.handle, signal);
    }

    // Real section titles replace the placeholder checkboxes, checked
    // according to whatever the URL says (or all checked, by default).
    renderCategoryFilterUI(sectionsWithProducts);

    // Final check right before the actual DOM swap — the most important one,
    // since everything up to this point was just building an off-DOM fragment.
    if (loadGuard.isStale(token)) {
      debugLog('renderApp aborted before DOM swap (stale token', token, ')');
      return;
    }

    await swapContentWithFade(content, fragment);
    updateNoMatchVisibility();

    if (loadGuard.isStale(token)) return; // something newer finished mid-fade
  };

  // ═══════════════════════════════════════════════════════════════
  // LOAD / INIT FLOW
  // ═══════════════════════════════════════════════════════════════

  // Builds the fetch URL for either mode. Category tags/vehicle fitment for
  // search mode are handled entirely server-side (see the proxy's
  // /api/search/query) — the front-end just passes q + vehicleNodeId.
  const buildCollectionUrl = (handleId, limit, materials) => {
    const apiParams = new URLSearchParams();
    apiParams.set('limit', limit);
    apiParams.set('country', country);
    apiParams.set('language', language);
    materials.forEach((m) => apiParams.append('material', m));
    return `${API_BASE_URL}/api/collection/${encodeURIComponent(handleId)}?${apiParams.toString()}`;
  };

  const buildSearchUrl = (searchTerm, vehicleNodeId, limit, materials) => {
    const apiParams = new URLSearchParams();
    apiParams.set('q', searchTerm);
    apiParams.set('limit', limit);
    apiParams.set('country', country);
    apiParams.set('language', language);
    if (vehicleNodeId) apiParams.set('vehicleNodeId', vehicleNodeId);
    materials.forEach((m) => apiParams.append('material', m));
    return `${API_BASE_URL}/api/collection/search/query?${apiParams.toString()}`;
  };

  // Shared by initial page load, vehicle-filter-changed, material changes,
  // and reset — fetches one collection's or one search's data and renders
  // it, respecting the race-condition guard.
  //
  // options: { mode: 'collection' | 'search', handle, handleId, searchTerm,
  //            vehicleNodeId, limit }
  const loadCollection = async (root, options) => {
    const { mode, handle = '', handleId, searchTerm, vehicleNodeId, limit } = options;
    const { token, signal } = loadGuard.startNewLoad();
    const component = document.getElementById('custom-collection-content');
    const parentLoader = document.getElementById('custom-collection-loader');
    const materials = getActiveMaterialsFromUrl();

    const url = mode === 'search'
      ? buildSearchUrl(searchTerm, vehicleNodeId, limit, materials)
      : buildCollectionUrl(handleId, limit, materials);

    debugLog('fetch start:', url, 'token:', token, 'mode:', mode, 'materials:', materials);

    try {
      const response = await fetchJSON(url, signal);
      if (loadGuard.isStale(token)) {
        debugLog('discarding stale response for token', token);
        return;
      }

      debugLog('fetch success, source:', response.source);
      const { data } = response;

      const categoriesRaw = data.categories;
      const allProducts = flattenProducts(categoriesRaw);
      const sections = mapSections(window.customCollectionSections || [], categoriesRaw, allProducts, limit);
      
      debugLog('sections ready:', sections.map((s) => s.title));

      const state = {
        mode,
        handle, // '' in search mode — product cards link to /products/{handle}
        handleId,
        searchTerm,
        vehicleNodeId,
        apiBase: API_BASE_URL,
        sections,
        categoriesRaw,
        allProducts,
        productsPerCategory: limit,
        fits: data.fits || undefined
      };

      await renderApp(root, state, token, signal);

      // Stored globally so the delegated "load more" click handler (attached
      // once, outside this function) can access the current state.
      if (!loadGuard.isStale(token)) window.__collectionState = state;
    } catch (err) {
      if (err.name === 'AbortError') {
        debugLog('fetch aborted (superseded by a newer request), token:', token);
        return;
      }
      debugLog('fetch error:', err);
      if (!loadGuard.isStale(token)) showError(root, 'Could not load products.');
    }

    component.classList.remove('loading');
    parentLoader.classList.add('hidden');
  };

  const init = () => {
    debugLog('init() start');

    const root = document.getElementById('custom-collection-app');
    if (!root) return;

    const { handle = '', pageType, searchTerms } = root.dataset;

    debugLog('config:', { handle, pageType, searchTerms, apiBase: API_BASE_URL });

    if (!API_BASE_URL) return showError(root, 'Missing API base URL.');

    if (pageType === 'search') {
      if (!searchTerms) return showError(root, 'Missing search term.');
      loadCollection(root, buildLoadOptions(root));
      return;
    }

    if (!handle) return showError(root, 'Missing collection handle.');
    loadCollection(root, buildLoadOptions(root));
  };

  // ═══════════════════════════════════════════════════════════════
  // EVENT LISTENERS
  // ═══════════════════════════════════════════════════════════════

  document.addEventListener('vehicle-filter-changed', (e) => {
    const { slug, carData, handleId } = e.detail;
    const root = document.getElementById('custom-collection-app');
    if (!root) return;

    const component = document.getElementById('custom-collection-content');
    debugLog('check -> ', e.detail, handleId);
    component.classList.add('loading');

    const hasGeneration = (carData?.generation?.length ?? 0) > 1;
    const limit = hasGeneration
      ? 500
      : parseInt(root.dataset.productsPerCategory || '0', 10);

    // On the search results page, a vehicle pick narrows the CURRENT search
    // (via vehicleNodeId) — it must NOT navigate to a /collections/... URL,
    // which would leave the search results entirely.
    if (root.dataset.pageType === 'search') {
      loadCollection(root, buildLoadOptions(root, { vehicleNodeId: handleId, limit }));
      return;
    }

    const newHandle = slug.replace('/collections/', '');

    // Keep the root's dataset in sync with the vehicle actually being
    // shown — material-filter-changed and the empty-state reset button both
    // read root.dataset.handle/generation later, and without this they'd
    // keep using whatever handle the page originally server-rendered with,
    // not the vehicle the person has since navigated to.
    root.dataset.handle = newHandle;
    root.dataset.handleId = handleId;
    root.dataset.generation = hasGeneration ? 'true' : '';

    loadCollection(root, { mode: 'collection', handle: newHandle, handleId, limit });
  });

  const commitSearchTermChange = (input) => {
    const root = document.getElementById('custom-collection-app');
    if (!root || root.dataset.pageType !== 'search') return;

    const newTerm = input.value.trim();
    if (!newTerm) return; // ignore empty submissions — keep the previous results rather than erroring

    if (newTerm === root.dataset.searchTerms) return; // no actual change — skip the re-fetch

    const icon = document.querySelector('.edit-field__icon');
    if (icon) icon.textContent = newTerm;

    root.dataset.searchTerms = newTerm;

    const params = new URLSearchParams(window.location.search);
    params.set('q', newTerm);
    const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    history.pushState(history.state, '', newUrl);

    const component = document.getElementById('custom-collection-content');
    if (component) component.classList.add('loading');

    loadCollection(root, buildLoadOptions(root, { searchTerm: newTerm }));
  };

  // Enter commits immediately (via blur, which triggers the 'change' handler
  // below) instead of requiring the user to click away first.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const input = e.target.closest('[data-edit-input]');
    if (!input) return;
    e.preventDefault();
    input.blur();
  });

  // Clicking away without pressing Enter still commits, same as any normal
  // form field — but only if the value actually changed (native 'change'
  // semantics already skip firing otherwise).
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-edit-input]');
    if (!input) return;
    commitSearchTermChange(input);
  });

  // Category checkboxes: client-side hide/show, no re-fetch. Material
  // checkboxes: always require a real re-fetch (server-side filtering), so
  // they're handled as a separate branch that calls loadCollection directly.
  document.addEventListener('change', (e) => {
    if (!e.target.matches('.multi-select__option')) return;

    const isMaterialCheckbox = !!e.target.closest('.material');

    if (isMaterialCheckbox) {
      const checkedMaterials = getCheckedMaterialValues();
      setActiveMaterialFiltersInUrl(checkedMaterials);
      updateMultiSelectToggleLabel('material');

      const root = document.getElementById('custom-collection-app');
      if (!root) return;

      const component = document.getElementById('custom-collection-content');
      if (component) component.classList.add('loading');

      loadCollection(root, buildLoadOptions(root));
      return;
    }

    const checkedValues = getCheckedCategoryValues();
    setActiveCategoryFiltersInUrl(checkedValues);
    applyCategoryFilterVisibility(checkedValues);
    updateMultiSelectToggleLabel('category');
    updateMultiSelectToggleLabel('material');
    updateNoMatchVisibility();
  });

  /**
   * LOAD MORE HANDLER
   * Delegated click listener — fetches the next page for ONE section only,
   * via the separate /load-more endpoint (collection or search variant).
   * Never re-fetches all categories.
   */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.load-more-btn');
    if (!btn) return;

    const state = window.__collectionState;
    const {
      sectionHandles: tags,
      cursor,
      collectionHandle,
      shopifyHandle,
      mode,
      searchTerm,
      vehicleNodeId,
    } = btn.dataset;
    const limit = parseInt(btn.dataset.limit, 10);

    debugLog('load-more click:', { tags, cursor, collectionHandle, shopifyHandle, mode });

    if (!cursor || cursor === 'undefined' || cursor === 'null') {
      debugLog('load-more aborted: invalid cursor');
      return;
    }

    btn.disabled = true;
    const originalText = getComponentButtonLabel(btn);
    setComponentButtonLabel(btn, textLabels.loadMoreLoading);

    const loadMoreParams = new URLSearchParams();
    loadMoreParams.set('tags', tags);
    loadMoreParams.set('cursor', cursor);
    loadMoreParams.set('limit', limit);
    loadMoreParams.set('country', country);
    loadMoreParams.set('language', language);

    // Same materials currently applied to the main load — without this,
    // "load more" would silently ignore the active material filter and
    // pull in unfiltered products.
    getActiveMaterialsFromUrl().forEach((m) =>
      loadMoreParams.append('material', m)
    );

    let url;
    if (mode === 'search') {
      loadMoreParams.set('q', searchTerm);
      if (vehicleNodeId) loadMoreParams.set('vehicleNodeId', vehicleNodeId);
      url = `${state.apiBase}/api/collection/search/query/load-more?${loadMoreParams.toString()}`;
    } else {
      url = `${state.apiBase}/api/collection/${encodeURIComponent(collectionHandle)}/load-more?${loadMoreParams.toString()}`;
    }

    try {
      const response = await fetchJSON(url);
      const newProducts = response.data.products;

      debugLog(
        'load-more success, products received:', newProducts.length,
        'hasNextPage:', response.data.hasNextPage
      );

      const ul = document.getElementById(btn.dataset.ulId);
      
      const cardHtmls = await fetchSectionCardHtmls(newProducts, shopifyHandle);

      cardHtmls.forEach((html) => {
        if (html == null) return;
        const li = document.createElement('li');
        li.className = 'grid__item';
        li.innerHTML = html;
        ul.appendChild(li);
      });

      if (!response.data.hasNextPage) {
        btn.parentElement.querySelector('.refine-selection-btn')?.remove();
        btn.remove();
      } else {
        btn.dataset.cursor = response.data.endCursor;
        btn.disabled = false;
        setComponentButtonLabel(btn, originalText);

        // Only nudge toward the vehicle selector once the user has clicked
        // Load more a few times (4th click onward) — not the very first time.
        const clickCount = (parseInt(btn.dataset.clickCount || '0', 10)) + 1;
        btn.dataset.clickCount = clickCount;
        if (clickCount >= 4) ensureRefineButton(btn);
      }
    } catch (err) {
      debugLog('load-more error:', err);
      btn.disabled = false;
      setComponentButtonLabel(btn, 'Error — try again');
    }
  });

  // ═══════════════════════════════════════════════════════════════
  // BOOTSTRAP
  // ═══════════════════════════════════════════════════════════════

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();